// @/hooks/use-prevent-auto-focus.ts
import { useRef, useCallback } from "react";

/**
 * A hook that prevents auto-focusing of elements in a dialog/modal.
 * This is useful when you want to prevent the first focusable element
 * from being automatically focused when a dialog opens.
 *
 * Based on shadcn-ui's solution for preventing unwanted autofocus behavior:
 * @see https://github.com/shadcn-ui/ui/discussions/2307#discussioncomment-13154396
 */
function usePreventAutoFocus<E extends HTMLElement = HTMLDivElement>() {
  const ref = useRef<E>(null);

  const onOpenAutoFocus = useCallback((event: Event) => {
    event.preventDefault();
    ref.current?.focus({ preventScroll: true });
  }, []);

  return { ref, onOpenAutoFocus, tabIndex: -1 as const };
}

export default usePreventAutoFocus;
